import projectModel from '../../../models/projects';
import constants from './constants';
import tooltips from './components/tooltips';

export default {
  prepareItemForBarMenu(column, gridColumns) {
    const isDateColumn = (column.name === 'start_date' || column.name === 'end_date');
    const isProjectColumns = column.name === 'project_name';

    const item = {
      id: column.name,
      option: false,
      icon: constants.STANDART_COLUMNS_ICONS[column.name],
      enabled: gridColumns[column.name] || false,
    };

    if (isDateColumn || isProjectColumns) {
      item.option = true;
      item.optionIcon = constants.STANDART_COLUMNS_ICONS.advancedOptions[column.name];
      item.optionValue = __(`gantt_grid_option_${column.name}`);
      item.enabledOption = gridColumns.advancedOptions && gridColumns.advancedOptions.hasOwnProperty(column.name) ? gridColumns.advancedOptions[column.name].check : 0;
    }

    if (column.isUserCustomColumn || column.custom) {
      let value = column.label;

      if (/&lt;|&gt;/.test(value)) {
        value = value.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      }

      item.value = value;
      item.icon = constants.CUSTOM_COLUMNS.find(item => item.id === column.columnType).icon;
      item.columnId = column.control_id;
      item.projectIds = column.ganttIds || column.projectIds;
      item.tooltip = tooltips.getTooltipById(item.id);
    } else {
      item.value = column.text || __(`gantt_grid_${column.name}`);
      item.tooltip = __(`${column.name}_custom_grid_tooltip`);
    }

    return item;
  },
  prepareListViewAllColumns(columns) {
    return columns.map(col => {
      const prepared = {
        name: col.id,
        text: col.text,
      };

      if (col.custom) {
        prepared.columnType = col.typeId;
        prepared.label = col.text;
        prepared.custom = col.custom;
        prepared.projectIds = col.ganttIds;
      }

      return prepared;
    });
  },
  prepareListViewEnabledColumns(columnIds) {
    const preparedData = {};

    columnIds.forEach(id => {
      preparedData[id] = true;

      if (typeof id === 'string' && id.includes('_extra')) {
        if (id.includes('start_date')) {
          preparedData.advancedOptions = {
            ...preparedData.advancedOptions,
            start_date: {
              check: true,
            },
          };
        }

        if (id.includes('end_date')) {
          preparedData.advancedOptions = {
            ...preparedData.advancedOptions,
            end_date: {
              check: true,
            },
          };
        }

        if (id.includes('project_name')) {
          preparedData.advancedOptions = {
            ...preparedData.advancedOptions,
            project_name: {
              check: true,
            },
          };
        }
      }
    });

    return preparedData;
  },
  getPricingColName(column) {
    if (column.name === 'total_price') {
      return 'costs';
    }
    if (column.name === 'overload') {
      return 'workload';
    }

    return column.name;
  },
  groupedStandartColumns(columns) {
    const groups = {
      icons: [],
      general: [],
      schedule: [],
      workload: [],
      budget: [],
    };

    function setGroup(group, cols) {
      constants.STANDART_COLUMNS_GROUPS[group.toUpperCase()].forEach(id => {
        const column = cols.find(item => item.id === id);

        if (column) {
          column.group = `${group}`;
          groups[group].push(column);
        }
      });
    }

    Object.keys(groups).forEach(key => {
      setGroup(key, columns);
    });

    for (const [key, value] of Object.entries(groups)) {
      if (!value.length) {
        delete groups[key];
      }
    }

    return groups;
  },
  getRandomColor(colors, usedColors) {
    let randomColor = colors.find(color => !usedColors.includes(color));

    if (!randomColor) {
      const randomIndex = Math.floor(Math.random() * colors.length);

      if (usedColors[usedColors.length - 1] === colors[randomIndex] && randomIndex > 0) {
        randomColor = colors[randomIndex - 1];
      } else {
        randomColor = colors[randomIndex];
      }
    }

    return randomColor;
  },
  prepareDataToDelete(column) {
    const projectId = projectModel.getActiveGanttId();

    return {
      id: column.id,
      type: column.type,
      team_id: column.team_id,
      options: null,
      ganttIds: [
        { id: projectId, status: 'delete' },
      ],
    };
  },
  prepareAllResourcesBeforeDelete(column) {
    const resultData = {
      ganttIds: null,
      id: column.id,
      isChanged: true,
      last_update: new Date(),
      last_update_by: user.id,
      options: null,
      team_id: column.team_id,
      type: column.type,
    };

    const preparedOptions = column.options.map(opt => ({
      ...opt,
      status: 'delete',
    }));

    return {
      ...resultData,
      options: preparedOptions,
    };
  },
  prepareOptionsBeforeUpdate(columnType, column, options) {
    const resultData = {
      ganttIds: null,
      id: column.id,
      isChanged: true,
      last_update: new Date(),
      last_update_by: user.id,
      options: null,
      team_id: column.team_id,
      type: column.type,
    };

    const optionCheckList = {
      multiselect: ['value', 'sort_order'],
      select: ['value', 'sort_order'],
      tags: ['color', 'value', 'sort_order'],
      colors: ['value', 'sort_order'],
    };

    const selectOptions = ['multiselect', 'select', 'tags', 'colors'];

    const defaultProps = {
      color: null,
      custom_columns_id: column.id,
      icon: null,
      is_default: 0,
      locale: null,
      resource_id: null,
      sort_order: null,
      value: null,
    };

    const currentOptions = column.options;
    const updatedOptions = [];
    let newOptions = [];
    let deletedOptions = [];

    let hasChanges = false;

    if (selectOptions.includes(columnType)) {
      if (currentOptions.length !== options.length) {
        hasChanges = true;
      }

      newOptions = options.filter(opt => !currentOptions.some(o => o.id === opt.id)).map(opt => {
        const option = {
          ...defaultProps,
          ...opt,
          status: 'add',
        };

        return option;
      });

      if (newOptions.length) {
        hasChanges = true;
      }

      deletedOptions = currentOptions.filter(opt => !options.some(o => o.id === opt.id)).map(opt => {
        const option = { ...opt };

        option.status = 'delete';

        return option;
      });

      currentOptions.forEach(opt => {
        const option = { ...opt };
        const newOption = options.find(i => i.id === opt.id);

        if (!newOption) return;

        optionCheckList[columnType].forEach(prop => {
          if (opt[prop] !== newOption[prop]) {
            option[prop] = newOption[prop];
            option.status = 'update';
          }
        });

        if (option.status && option.status === 'update') {
          updatedOptions.push(option);
          hasChanges = true;
        }
      });

      resultData.options = updatedOptions.concat(newOptions, deletedOptions);
    }

    if (columnType === 'resources') {
      const preparedOptions = options.map((opt, index) => ({
        ...defaultProps,
        resource_id: opt.id,
        sort_order: index,
      }));

      if (preparedOptions.length === currentOptions.length) {
        const filtered = preparedOptions.filter(opt => currentOptions.find(item => item.resource_id === opt.resource_id));

        if (filtered.length !== currentOptions.length) {
          hasChanges = true;
        }
      } else {
        hasChanges = true;
      }

      newOptions = preparedOptions.filter(opt => !currentOptions.some(o => o.resource_id === opt.resource_id)).map((opt, index) => {
        const option = {
          ...defaultProps,
          resource_id: opt.resource_id,
          sort_order: index,
          status: 'add',
        };

        return option;
      });

      deletedOptions = currentOptions.filter(opt => !preparedOptions.some(o => o.resource_id === opt.resource_id)).map(opt => {
        const option = {
          ...opt,
          resource_id: opt.resource_id,
          status: 'delete',
        };

        return option;
      });

      resultData.options = newOptions.concat(deletedOptions);
    }

    if (columnType === 'number') {
      resultData.custom_column_sub_types_id = options[0].id;

      if (options[0].id !== column.sub_type) {
        hasChanges = true;
      }
    }

    if (!hasChanges) return null;

    return resultData;
  },
  prepareChanges(column, name, optionChanges) {
    const defaultData = {
      ganttIds: null,
      id: column.id,
      isChanged: true,
      last_update: new Date(),
      last_update_by: user.id,
      options: null,
      team_id: column.team_id,
      type: column.type,
    };

    let dataToServer = null;

    if (!optionChanges) {
      dataToServer = {
        ...defaultData,
        name,
      };
    } else {
      dataToServer = {
        ...optionChanges,
        name,
      };
    }

    return dataToServer;
  },
  prepareNewColumnDataForSave(data) {
    const projectId = projectModel.getActiveGanttId();

    const dataForServer = {
      ganttIds: [projectId],
      name: data.name,
      type: data.type,
      team_id: user.team.id,
    };

    if (data.options.length) {
      if (data.type === 3) { // Type: Number
        dataForServer.custom_column_sub_types_id = data.options[0].id;
      } else {
        const preparedOptions = data.options.map(opt => {
          const {
            id, is_default, sort_order, value, color, resource_id,
          } = opt;

          const optionForServer = {
            id,
            is_default,
            sort_order,
          };

          if (value) {
            optionForServer.value = value;
          }

          if (resource_id) {
            optionForServer.resource_id = resource_id;
            delete optionForServer.id;
          }

          if (color) {
            optionForServer.color = color;
          }

          return optionForServer;
        });

        dataForServer.options = preparedOptions;
      }
    }

    return dataForServer;
  },
  prepareDataForReuseColumn(columnId) {
    const projectId = projectModel.getActiveGanttId();

    const data = {
      id: columnId,
      ganttIds: [
        { id: projectId, status: 'add' },
      ],
      last_update: new Date(),
    };

    return data;
  },
  getColumnGroupNameById(id) {
    let groupName = '';

    Object.entries(constants.STANDART_COLUMNS_GROUPS).forEach(([key, value]) => {
      if (value.includes(id)) {
        groupName = key;
      }
    });

    return groupName.toLocaleLowerCase();
  },
};
